/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
// tslint:disable:max-line-length
import { BoxBufferGeometry, BufferGeometry, CircleBufferGeometry, ConeBufferGeometry, CylinderBufferGeometry, ExtrudeGeometry, Geometry, PlaneBufferGeometry, RingBufferGeometry, Shape, ShapeBufferGeometry, SphereBufferGeometry, Vector3, EdgesGeometry } from 'three';
import { SVGPathReader } from '..';
/**
 * Helper for creating geometries for all solids
 */
export class Solids {
    static createOutline(boxGeometry, thresholdAngle = 1) {
        return new EdgesGeometry(boxGeometry, thresholdAngle);
    }
    static createSector(innerRadius, outerRadius, thetaSegments, phiSegments, thetaStart, thetaLength) {
        return new RingBufferGeometry(innerRadius, outerRadius, thetaSegments, phiSegments, thetaStart, thetaLength);
    }
    static createBox(sizeX = 1.0, sizeY = 1.0, sizeZ = 1.0, segmentsX = 1.0, segmentsY = 1.0, segmentsZ = 1.0) {
        return new BoxBufferGeometry(sizeX, sizeY, sizeZ, segmentsX, segmentsY, segmentsZ);
    }
    static createCircle(radius = 0.5, segments = 32.0, thetaStart = 0, thetaLength = 2.0 * Math.PI) {
        return new CircleBufferGeometry(radius, segments, thetaStart, thetaLength);
    }
    static createCone(radius = 0.5, height = 1.0, radialSegments = 32, heightSegments, openEnded, thetaStart, thetaLength) {
        return new ConeBufferGeometry(radius, height, radialSegments, heightSegments, openEnded, thetaStart, thetaLength);
    }
    static createCylinder(radiusTop = 0.5, radiusBottom = 0.5, height = 1.0, radialSegments = 32, heightSegments, openEnded) {
        return new CylinderBufferGeometry(radiusTop, radiusBottom, height, radialSegments, heightSegments, openEnded);
    }
    static createSphere(radius = 0.5, widthSegments = 32, heightSegments = 32, phiStart, phiLength, thetaStart, thetaLength) {
        return new SphereBufferGeometry(radius, widthSegments, heightSegments, phiStart, phiLength, thetaStart, thetaLength);
    }
    static createLineSegment(x1, y1, z1, x2, y2, z2) {
        const geometry = new Geometry();
        geometry.vertices.push(new Vector3(x1, y1, z1), new Vector3(x2, y2, z2));
        return geometry;
    }
    static createPlane(size, x, y, z, d) {
        const geo = this.createRectangle(size, size);
        const newDir = new Vector3(x, y, z);
        newDir.normalize();
        const pos = newDir.clone().multiplyScalar(d);
        geo.lookAt(newDir);
        geo.translate(pos.x, pos.y, pos.z);
        return geo;
    }
    static createPolygonUnsafe(polylist) {
        const shape = new Shape();
        if (polylist.length >= 3) {
            const e1 = polylist[0];
            shape.moveTo(e1.x, e1.y);
            for (let i = 1; i < polylist.length; i++) {
                const e = polylist[i];
                shape.lineTo(e.x, e.y);
            }
            shape.lineTo(e1.x, e1.y); // close path
        }
        return this.createSurfaceFromShape(shape);
    }
    static createPolylineUnsafe(polylist, isClosed = false) {
        const geometry = new Geometry();
        polylist.forEach((e) => {
            geometry.vertices.push(new Vector3(e.x, e.y, e.z || 0.0));
        });
        if (isClosed && polylist.length > 2) {
            const e = polylist[0];
            geometry.vertices.push(new Vector3(e.x, e.y, e.z || 0.0));
        }
        return geometry;
    }
    static createPolyline(polylist) {
        const geometry = new Geometry();
        geometry.vertices = polylist;
        return geometry;
    }
    static createRectangle(xSize = 1, ySize = 1, xSegments, ySegments) {
        return new PlaneBufferGeometry(xSize, ySize, xSegments, ySegments);
    }
    static createSurfaceFromShape(shape) {
        return new ShapeBufferGeometry(shape);
    }
    static createExtrudedObjectFromShape(shape, extrudeSettings) {
        return new ExtrudeGeometry(shape, extrudeSettings);
    }
    static createPointsFromShape(shape, spaced = false, divisions) {
        let points;
        if (spaced) {
            points = shape.getSpacedPoints(divisions);
        }
        else {
            points = shape.getPoints();
        }
        const geometry = new BufferGeometry().setFromPoints(points);
        return geometry;
    }
    static shapeFromSVGPath(svgPath) {
        const path = new Shape();
        const svgReader = new SVGPathReader(path);
        svgReader.readSVGPath(svgPath);
        return path;
    }
}
//# sourceMappingURL=Solids.js.map